import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'components/Commons/Translation'
import { HOME } from 'routes'
import { USER_PREFERENCE_TYPE } from 'utils/users'

import { useApplication, UNSAFE_getApplicationContext, persistApplicationContext } from 'context/Application'
import { useProfile, getActiveOrganizations } from 'context/Profile'
import { useApplicationOrganization } from 'context/Organization'

import { LoadingDots } from '@nfinite/react-hubstairs/components'

import PaginatedList, { PaginatedListProvider, usePaginatedList } from 'components/Commons/PaginatedList'

import CardGrid from 'components/Commons/Grid/Cards'
import ModalSwitch from './ModalSwitch'

// eslint-disable-next-line no-restricted-imports
import { useQuery as useQueryApolloClient } from '@apollo/client'
import { useMutation } from 'requests'
import { GET_PAGINATED_ORGANIZATIONS, GET_PROFILE_ORGANIZATIONS, GET_MY_PAGINATED_ORGANIZATIONS } from './query'
import { UPDATE_MY_USER_PREFERENCE } from './mutation'

import { Container, Content, FiltersContainer, Search, ToggleComponent, SearchWrapper, Results } from './styled'

import translations from './translations'
import OrganizationCard, { OrganizationCardPlaceholder } from './Card'
import UserInfo from './UserInfo'
import { useGetNumberOfResults } from 'components/Commons/FiltersList/hooks/useGetNumberOfResults'

function OrganizationPlaceholder() {
  return <OrganizationCardPlaceholder />
}

function Organization({ item, role, onSelect, onClose }) {
  const { organization } = useApplicationOrganization()
  const { id } = item

  const isActive = item.id === organization?.id

  function handleSelect() {
    if (isActive) {
      onClose?.()
      return
    }
    onSelect(item, role)
  }

  return <OrganizationCard key={id} isActive={isActive} organization={item} onClick={handleSelect} role={role} />
}

function useUpdateTestingPreferences({ refetchQueries, awaitRefetchQueries = true }) {
  const { profile, setProfile } = useProfile()
  const [update, { loading }] = useMutation(UPDATE_MY_USER_PREFERENCE, {
    refetchQueries,
    awaitRefetchQueries,
    onCompleted: data => {
      setProfile({
        ...profile,
        userPreferences: data.user.userPreferences,
      })
    },
  })

  const updateTestingPreferences = useCallback(
    async hideTestOrganizations => {
      try {
        update({
          variables: {
            userId: profile.id,
            userPreference: { type: USER_PREFERENCE_TYPE.ORGANIZATIONS_TESTING, value: hideTestOrganizations },
          },
        })
      } catch (e) {
        // silently catch error
      }
    },
    [profile, update],
  )

  return [updateTestingPreferences, { loading }]
}

export function TestingOrganizationsToggle({ query }) {
  const { profile } = useProfile()
  const { hideTestOrganizations } = profile.userPreferences
  const { filters, sort } = usePaginatedList()

  const refetchQueries = [{ query, variables: { paging: { skip: 0, limit: 30 }, filters, sort } }]

  const [updateTestingPreferences, { loading }] = useUpdateTestingPreferences({ refetchQueries })

  function onChange(value) {
    updateTestingPreferences(value)
  }

  return (
    <ToggleComponent
      dataTestId="toggle_testing_organization"
      label={translations.testingOrganizationsToggle}
      checked={hideTestOrganizations}
      disabled={loading}
      onChange={onChange}
    />
  )
}

function PickerGlobalContent({ onSelect, onClose }) {
  const numberOfResults = useGetNumberOfResults()
  return (
    <>
      <FiltersContainer>
        <Results>
          <FormattedMessage {...translations.totalResults} values={{ count: numberOfResults }} />
        </Results>
        <SearchWrapper>
          <Search size="sm" placeholder={translations.searchPlaceholder} />
        </SearchWrapper>
      </FiltersContainer>
      <PaginatedList
        Container={CardGrid.ExtraLarge}
        placeholder={<OrganizationPlaceholder />}
        renderItem={item => <Organization key={item.id} item={item} onSelect={onSelect} onClose={onClose} />}
      />
    </>
  )
}

function PickerUserGlobal({ onSelect, onClose, applicationOrganization }) {
  return (
    <PaginatedListProvider
      query={GET_PAGINATED_ORGANIZATIONS}
      initialFilters={{ hideDeleted: true }}
      initialSort={{
        userPreferences: true,
        ...(applicationOrganization?.id && { organizationsPriority: [applicationOrganization.id] }),
        name: 'ASC',
      }}
      ignoreSession
    >
      <PickerGlobalContent onSelect={onSelect} onClose={onClose} />
    </PaginatedListProvider>
  )
}

function PickerUserContent({ onSelect, onClose, organizations }) {
  const numberOfResults = useGetNumberOfResults()
  return (
    <>
      <FiltersContainer>
        <Results>
          <FormattedMessage {...translations.totalResults} values={{ count: numberOfResults }} />
        </Results>
        <SearchWrapper>
          <Search size="sm" placeholder={translations.searchPlaceholder} />
        </SearchWrapper>
      </FiltersContainer>
      <PaginatedList
        Container={CardGrid.ExtraLarge}
        placeholder={<OrganizationPlaceholder />}
        renderItem={item => {
          const role = organizations.find(({ organization }) => organization.id === item.id).role
          return <Organization key={item.id} item={item} role={role} onSelect={onSelect} onClose={onClose} />
        }}
      />
    </>
  )
}

/**
 * We dont use our abstraction of the use query hook to avoid sending the hub-organization header on the /me query
 * We want to be up to date with the active organizations of the user and therefore wait for the query to terminate
 * before we display the result
 */
function PickerUserOrganization({ onSelect, onClose, applicationOrganization }) {
  const { data } = useQueryApolloClient(GET_PROFILE_ORGANIZATIONS, { fetchPolicy: 'network-only' })
  const organizations = getActiveOrganizations(data?.me)
  const organizationIds = organizations?.map(({ organization }) => organization.id)

  return !organizations ? (
    <LoadingDots />
  ) : (
    <PaginatedListProvider
      query={GET_MY_PAGINATED_ORGANIZATIONS}
      initialFilters={{ ids: organizationIds, hideDeleted: true }}
      initialSort={{
        userPreferences: true,
        ...(applicationOrganization?.id && { organizationsPriority: [applicationOrganization.id] }),
        name: 'ASC',
      }}
      ignoreSession
      options={{
        // We don't want to be in an organizationContext for this query to get all the organizations of the user
        context: { organizationId: null },
      }}
    >
      <PickerUserContent onSelect={onSelect} onClose={onClose} organizations={organizations} />
    </PaginatedListProvider>
  )
}

function OrganizationPicker({ onClose, setOrganization, loading }) {
  const { organization } = useApplicationOrganization()
  const { isGlobalRole } = useProfile()

  function onSelect(item, role) {
    setOrganization(item, role)
  }

  const PickerComponent = isGlobalRole ? PickerUserGlobal : PickerUserOrganization

  return (
    <Container>
      <Content>
        {loading ? (
          <LoadingDots />
        ) : (
          <PickerComponent onSelect={onSelect} onClose={onClose} applicationOrganization={organization} />
        )}
      </Content>
    </Container>
  )
}

function useUpdateOrganizationPreferences(options) {
  const { profile } = useProfile()
  const [update] = useMutation(UPDATE_MY_USER_PREFERENCE, options)

  const updateOrganizationPreferences = useCallback(
    async organizationId => {
      try {
        update({
          variables: {
            userId: profile.id,
            userPreference: { type: USER_PREFERENCE_TYPE.ORGANIZATIONS, id: organizationId },
          },
        })
      } catch (e) {
        // silently catch error
      }
    },
    [profile, update],
  )

  return updateOrganizationPreferences
}

function OrganizationPickerSwitch({ onClose }) {
  const [loading, setLoading] = useState(false)
  const [completed, setCompleted] = useState(false)

  const updateOrganizationPreferences = useUpdateOrganizationPreferences({
    onCompleted,
    onError: onCompleted,
  })

  function onCompleted() {
    setCompleted(true)
  }

  // we wait for the mutation te be in flight before we restart the app
  useEffect(() => {
    if (completed) {
      window.location.assign(`${window.location.origin}${HOME()}?switched=1`)
    }
  }, [completed])

  const setOrganization = useCallback(
    async organization => {
      setLoading(true)
      sessionStorage.clear()

      const current = UNSAFE_getApplicationContext()
      const updatedContext = Object.assign(current, {
        organization: organization.id,
      })

      persistApplicationContext(updatedContext)

      updateOrganizationPreferences(organization.id)
    },
    [updateOrganizationPreferences],
  )

  return <OrganizationPicker onClose={onClose} setOrganization={setOrganization} loading={loading} />
}

export function OrganizationPickerModal({ id, open, onClose }) {
  return (
    <ModalSwitch id={id} open={open} onClose={onClose}>
      <UserInfo />
      <OrganizationPickerSwitch onClose={onClose} />
    </ModalSwitch>
  )
}

function OrganizationPickerBlocking() {
  const { setOrganization: setOrganizationContext } = useApplication()
  const updateOrganizationPreferences = useUpdateOrganizationPreferences()

  const setOrganization = useCallback(
    async organization => {
      updateOrganizationPreferences(organization.id)
      setOrganizationContext(organization.id)
    },
    [setOrganizationContext, updateOrganizationPreferences],
  )

  return <OrganizationPicker setOrganization={setOrganization} />
}

export function OrganizationPickerModalBlocking({ id }) {
  return (
    <ModalSwitch id={id} open={true}>
      <UserInfo />
      <OrganizationPickerBlocking />
    </ModalSwitch>
  )
}

export default OrganizationPicker
